import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ChevronRight,
  Download,
  ExternalLink,
  File as FileIcon,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  Languages,
  Link2Off,
  Presentation,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError } from '../lib/api';
import { formatBytes, formatDate, formatDateTime } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import { VigLogo } from '../components/Logo';
import { Button, Card, LoadingBlock, useToast } from '../components/ui';

/**
 * A Viganium Drive link's page (2026-11-02): what someone at Viganium shared —
 * by a public link, or emailed to one of a client's contacts. Open to anyone who
 * has the link, signed in or not: the link is the permission, and nothing here
 * changes anything. A folder opens folder by folder, within the one shared; each
 * file downloads, and a picture, a PDF, a recording or a video opens in a tab.
 */

const BASE = '/api/v1/drive/public';

interface SharedRow {
  id: string;
  kind: 'FOLDER' | 'FILE';
  name: string;
  updatedAt: string;
  sizeBytes: number | null;
  mimeType: string | null;
  viewable: boolean;
  items: number | null;
}

interface SharedFolder {
  id: string;
  name: string;
  /** From the folder the link shares down to this one's parent. */
  path: Array<{ id: string; name: string }>;
  items: SharedRow[];
}

interface Shared {
  link: { kind: 'PUBLIC' | 'CONTACT'; expiresAt: string | null; recipient: string | null };
  sharedBy: { fullName: string; fullNameAr: string | null };
  company: { name: string; nameAr: string };
  item: SharedRow;
  folder: SharedFolder | null;
}

/** No session: plain requests, and our error envelope read the same way. */
async function read<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/${path}`, init);
  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error ?? { code: 'NETWORK_ERROR', message: `Request failed (${res.status}).` });
  }
  return payload?.data as T;
}

function iconFor(row: SharedRow) {
  if (row.kind === 'FOLDER') return Folder;
  const mime = row.mimeType ?? '';
  if (mime.startsWith('image/')) return FileImage;
  if (mime.startsWith('audio/')) return FileAudio;
  if (mime.startsWith('video/')) return FileVideo;
  if (mime.includes('sheet') || mime.includes('excel') || mime === 'text/csv') return FileSpreadsheet;
  if (mime.includes('presentation') || mime.includes('powerpoint')) return Presentation;
  if (mime.includes('zip')) return FileArchive;
  if (mime === 'application/pdf' || mime.includes('word') || mime === 'text/plain') return FileText;
  return FileIcon;
}

const UNAVAILABLE: Record<string, TranslationKey> = {
  NOT_FOUND: 'share.unavailable.NOT_FOUND',
  REVOKED: 'share.unavailable.REVOKED',
  EXPIRED: 'share.unavailable.EXPIRED',
  GONE: 'share.unavailable.GONE',
};

export function SharePage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const folderId = params.get('folder');
  const { t, locale, setLocale } = useI18n();

  const shared = useQuery({
    queryKey: ['share', token],
    queryFn: () => read<Shared>(token),
    retry: false,
    staleTime: Infinity,
  });
  const inner = useQuery({
    queryKey: ['share', token, 'folder', folderId],
    queryFn: () => read<{ folder: SharedFolder }>(`${token}/folders/${folderId}`),
    enabled: !!folderId && !!shared.data,
    retry: false,
  });

  const data = shared.data;
  const folder = folderId ? inner.data?.folder : data?.folder;
  const error = shared.error ?? (folderId ? inner.error : null);
  const reason =
    error instanceof ApiError && error.code === 'DRIVE_LINK_UNAVAILABLE'
      ? ((error.details as { reason?: string } | undefined)?.reason ?? 'NOT_FOUND')
      : error
        ? 'ERROR'
        : null;

  const company = data ? (locale === 'ar' ? data.company.nameAr : data.company.name) : '';
  const sender = data ? (locale === 'ar' && data.sharedBy.fullNameAr) || data.sharedBy.fullName : '';
  const file = (row: SharedRow, how: 'download' | 'view') => `${BASE}/${token}/files/${row.id}/${how}`;

  return (
    <div className="min-h-full bg-[var(--surface-page)]">
      <header className="border-b hairline bg-[var(--surface-raised)]">
        <div className="mx-auto flex h-16 max-w-5xl items-center justify-between gap-3 px-4">
          <VigLogo className="h-7 w-auto text-[var(--text-primary)]" title="Viganium" />
          <button
            type="button"
            onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
            lang={locale === 'en' ? 'ar' : 'en'}
            className="flex h-9 items-center gap-1.5 rounded-sm px-2 text-body-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
          >
            <Languages className="h-4 w-4" aria-hidden />
            {locale === 'en' ? 'العربية' : 'English'}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        {shared.isLoading ? (
          <LoadingBlock />
        ) : reason ? (
          <Card className="mx-auto max-w-lg text-center">
            <Link2Off className="mx-auto mb-3 h-9 w-9 text-[var(--text-tertiary)]" aria-hidden />
            <h1 className="text-h2 text-[var(--text-primary)]">
              {t(UNAVAILABLE[reason] ?? 'share.unavailable.ERROR')}
            </h1>
            <p className="mt-2 text-body-sm text-muted">
              {t(reason === 'ERROR' ? 'share.unavailable.tryAgain' : 'share.unavailable.hint')}
            </p>
            {folderId && reason !== 'ERROR' && (
              <Link to={`/share/${token}`} className="mt-4 inline-block text-body-sm text-primary-600 hover:underline dark:text-primary-400">
                {t('share.backToStart')}
              </Link>
            )}
          </Card>
        ) : data ? (
          <>
            <div className="mb-6">
              <p className="text-mono-sm uppercase tracking-widest text-[var(--text-tertiary)]">{t('share.kicker')}</p>
              <h1 className="mt-1 break-words text-h1 text-[var(--text-primary)]">{data.item.name}</h1>
              <p className="mt-2 text-body text-muted">
                {t('share.from', { name: sender, company })}
                {data.link.recipient && <> · {t('share.forYou', { name: data.link.recipient })}</>}
              </p>
              {data.link.expiresAt && (
                <p className="mt-1 text-body-sm text-faint">{t('share.until', { date: formatDate(data.link.expiresAt, locale) })}</p>
              )}
            </div>

            {data.item.kind === 'FILE' ? (
              <SharedFile row={data.item} view={file(data.item, 'view')} download={file(data.item, 'download')} />
            ) : (
              <Card padded={false}>
                {folderId && inner.isLoading ? (
                  <LoadingBlock />
                ) : folder ? (
                  <>
                    <Crumbs
                      token={token}
                      root={data.item}
                      folder={folder}
                      inside={!!folderId}
                      action={folder.items.length > 0 && <DownloadAll token={token} folderId={folder.id} />}
                    />
                    {!folder.items.length ? (
                      <p className="px-5 py-12 text-center text-body-sm text-muted">{t('share.empty')}</p>
                    ) : (
                      <ul className="divide-y divide-[var(--line)]">
                        {folder.items.map((row) => {
                          const Icon = iconFor(row);
                          return (
                            <li key={row.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                              <Icon
                                className={clsx(
                                  'h-5 w-5 shrink-0',
                                  row.kind === 'FOLDER' ? 'text-warning-fg dark:text-warning-dark' : 'text-[var(--text-tertiary)]',
                                )}
                                aria-hidden
                              />
                              <div className="min-w-0 flex-1">
                                {row.kind === 'FOLDER' ? (
                                  <Link
                                    to={`/share/${token}?folder=${row.id}`}
                                    className="block truncate font-medium text-[var(--text-primary)] hover:underline"
                                  >
                                    {row.name}
                                  </Link>
                                ) : (
                                  <span className="block truncate font-medium text-[var(--text-primary)]" title={row.name}>
                                    {row.name}
                                  </span>
                                )}
                                <span className="text-caption text-faint" title={formatDateTime(row.updatedAt, locale)}>
                                  {row.kind === 'FOLDER'
                                    ? t('share.items', { n: row.items ?? 0 })
                                    : `${formatBytes(row.sizeBytes, locale)} · ${formatDate(row.updatedAt, locale)}`}
                                </span>
                              </div>
                              {row.kind === 'FILE' && (
                                <div className="flex items-center gap-2">
                                  {row.viewable && (
                                    <a href={file(row, 'view')} target="_blank" rel="noreferrer">
                                      <Button size="sm" icon={<ExternalLink className="h-3.5 w-3.5" aria-hidden />}>
                                        {t('share.view')}
                                      </Button>
                                    </a>
                                  )}
                                  <a href={file(row, 'download')}>
                                    <Button size="sm" variant="primary" icon={<Download className="h-3.5 w-3.5" aria-hidden />}>
                                      {t('share.download')}
                                    </Button>
                                  </a>
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                ) : null}
              </Card>
            )}

            <p className="mt-8 text-center text-caption text-faint">{t('share.footer', { company })}</p>
          </>
        ) : null}
      </main>
    </div>
  );
}

/** The folder the link shares › each folder below it, down to the one open — and what can be done with that one. */
function Crumbs({
  token,
  root,
  folder,
  inside,
  action,
}: {
  token: string;
  root: SharedRow;
  folder: SharedFolder;
  inside: boolean;
  action?: ReactNode;
}) {
  const { t } = useI18n();
  const trail = inside ? [...folder.path, { id: folder.id, name: folder.name }] : [{ id: root.id, name: root.name }];
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b hairline px-5 py-3">
      <nav aria-label={t('share.where')} className="flex flex-wrap items-center gap-1 text-body-sm">
        {trail.map((p, i) => {
          const last = i === trail.length - 1;
          const to = p.id === root.id ? `/share/${token}` : `/share/${token}?folder=${p.id}`;
          return (
            <span key={p.id} className="inline-flex items-center gap-1">
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 flip-rtl text-faint" aria-hidden />}
              {last ? (
                <span className="font-medium text-[var(--text-primary)]">{p.name}</span>
              ) : (
                <Link to={to} className="text-muted hover:text-[var(--text-primary)] hover:underline">
                  {p.name}
                </Link>
              )}
            </span>
          );
        })}
      </nav>
      {action}
    </div>
  );
}

/**
 * The folder open, with everything in it, as one .zip (2026-11-03). Asked first,
 * so the page can say why it cannot be had — the link no longer opens, nothing
 * is in it, more than one download holds, many going out just now — then the
 * browser saves what the link sends, straight into its downloads however large.
 */
function DownloadAll({ token, folderId }: { token: string; folderId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const start = () => {
    setBusy(true);
    read<{ url: string; name: string }>(`${token}/folders/${folderId}/download`, { method: 'POST' })
      .then((zip) => save(zip.url, zip.name))
      .catch((e: unknown) => toast.error(t(refusal(e))))
      .finally(() => setBusy(false));
  };
  return (
    <Button
      size="sm"
      icon={<Download className="h-3.5 w-3.5" aria-hidden />}
      loading={busy}
      title={t('share.downloadAllHint')}
      onClick={start}
    >
      {t('share.downloadAll')}
    </Button>
  );
}

/** Why a folder's .zip cannot be had, in words. */
function refusal(e: unknown): TranslationKey {
  if (!(e instanceof ApiError)) return 'share.unavailable.tryAgain';
  const reason = (e.details as { reason?: string } | undefined)?.reason ?? '';
  if (e.code === 'DRIVE_DOWNLOAD_REFUSED') return DOWNLOAD_REFUSED.get(reason) ?? 'share.unavailable.tryAgain';
  if (e.code === 'DRIVE_LINK_UNAVAILABLE') return UNAVAILABLE[reason] ?? 'share.unavailable.NOT_FOUND';
  // Many going out at once, or this browser asking too often.
  if (e.status === 429) return 'share.downloadBusy';
  return 'share.unavailable.tryAgain';
}

const DOWNLOAD_REFUSED = new Map<string, TranslationKey>([
  ['EMPTY', 'share.downloadRefused.EMPTY'],
  ['TOO_BIG', 'share.downloadRefused.TOO_BIG'],
  ['TOO_MANY', 'share.downloadRefused.TOO_MANY'],
]);

/** Has the browser save what a link sends, under `name`, without leaving the page. */
function save(url: string, name: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** One file shared on its own: shown when it can be, and downloaded. */
function SharedFile({ row, view, download }: { row: SharedRow; view: string; download: string }) {
  const { t, locale } = useI18n();
  const mime = row.mimeType ?? '';
  const Icon = iconFor(row);
  const picture = /^image\/(png|jpeg|gif|webp)$/.test(mime);
  const video = /^video\/(mp4|x-m4v|quicktime|webm|ogg)$/.test(mime);
  const audio = mime.startsWith('audio/');
  return (
    <Card>
      {picture ? (
        <img src={view} alt={row.name} className="mx-auto mb-5 max-h-[65vh] max-w-full rounded-md object-contain" />
      ) : video ? (
        <video src={view} controls preload="metadata" className="mx-auto mb-5 max-h-[65vh] w-full rounded-md bg-black" />
      ) : audio ? (
        <audio src={view} controls preload="metadata" className="mb-5 w-full" />
      ) : (
        <Icon className="mx-auto mb-4 h-14 w-14 text-[var(--text-tertiary)]" aria-hidden />
      )}
      <div className="flex flex-wrap items-center justify-center gap-3">
        <span className="text-body-sm text-muted tabular">{formatBytes(row.sizeBytes, locale)}</span>
        {row.viewable && !picture && !video && !audio && (
          <a href={view} target="_blank" rel="noreferrer">
            <Button icon={<ExternalLink className="h-4 w-4" aria-hidden />}>{t('share.view')}</Button>
          </a>
        )}
        <a href={download}>
          <Button variant="primary" size="lg" icon={<Download className="h-4 w-4" aria-hidden />}>
            {t('share.download')}
          </Button>
        </a>
      </div>
    </Card>
  );
}
